/** Экраны витрины: меню, раздел, товар, сообщение, поиск.
 *
 * Один принцип на всех: покупатель видит фото и цену первыми, а выбор вкуса и
 * кнопка «Заказать» — на расстоянии одного движения. Жёсткое ограничение
 * продукта — «заказывать не должно стать медленнее», поэтому ни один экран не
 * добавляет лишнего шага между «увидел» и «заказал».
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Catalog, Menu, Product, ProductList, Reward } from '@/shared/types';
import { Button, Note } from '@/shared/ui';
import { openManager, tap } from '@/shared/tg';
import type { CartApi } from './cart';
import { count, countLabel } from '@/shared/plural';
import { RichBody, renderText } from '@/shared/richtext';
import { rememberTile, runMorph } from './morph';
import type { Screen } from './nav';

/** «Вы перешли в меню: X» — служебная реплика чата, в витрине это шум. */
const CHAT_GREETING = /^Вы перешли в меню:/i;

/** Последнее слово (обычно эмодзи) не должно повиснуть одно на новой строке. */
function noOrphan(text: string): string {
  return text.replace(/ (?=\S+$)/, ' ');
}

export function variantLabel(list: ProductList | undefined): string {
  return list?.variant_label || 'Вкус';
}

/** «3 вкуса», «2 варианта» — подсказка, что внутри есть выбор. */
function variantBadge(product: Product, list: ProductList | undefined): string | null {
  const total = product.variants.length;
  if (!total) return null;
  return countLabel(total, variantLabel(list).toLowerCase());
}

const Chevron = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

function Photo({ product, small }: { product: Product; small?: boolean }) {
  const src = small ? product.thumb ?? product.image : product.image;
  if (!src) {
    return (
      <div className="grid h-full min-h-24 place-items-center bg-glass text-xs text-hint">
        {product.stale_image ? 'Фото обновляется' : 'Без фото'}
      </div>
    );
  }
  return <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />;
}

/* ------------------------------------------------------------------ меню */

export function MenuScreen({
  menu,
  catalog,
  onOpen,
}: {
  menu: Menu;
  catalog: Catalog;
  onOpen: (screen: Screen) => void;
}) {
  const intro = (menu.message_text || '').trim();
  const showIntro = intro && !CHAT_GREETING.test(intro);

  return (
    <>
      {showIntro &&
        (intro.length <= 60 ? (
          <Note>{renderText(intro)}</Note>
        ) : (
          <RichBody
            text={intro}
            className="glass wet-edge mb-2 rounded-card p-4"
          />
        ))}

      {/* Две колонки: категорий обычно четыре-шесть, и в один столбец они
          уезжали под сгиб. На совсем узком экране плитка снова во всю ширину —
          иначе название переносится по слогам. */}
      <div className="grid grid-cols-2 gap-2.5 max-[380px]:grid-cols-1">
        {menu.buttons.map((button, index) => (
          <button
            key={button.id}
            type="button"
            onClick={() => {
              tap();
              if (button.action_type === 'submenu') {
                onOpen({ type: 'menu', id: button.target_id, title: button.text });
              } else if (button.action_type === 'product_list') {
                onOpen({ type: 'list', id: button.target_id, title: button.text });
              } else {
                onOpen({
                  type: 'message',
                  id: button.target_id,
                  title: button.text,
                  text: button.message_text ?? '',
                });
              }
            }}
            style={{ animationDelay: `${index * 28}ms` }}
            className="group glass wet-edge animate-rise flex items-center justify-between gap-2 overflow-hidden rounded-card px-[15px] py-4 text-left text-[15px] font-[620] transition-[transform,background-color,border-color] duration-150 ease-glass active:scale-[.975] active:border-mint active:bg-glass-active"
          >
            <span className="min-w-0 flex-1">{noOrphan(button.text)}</span>
            <span className="inline-flex text-mint transition-transform duration-200 ease-glass group-active:translate-x-[3px]">
              <Chevron />
            </span>
          </button>
        ))}
      </div>

      <ManagerButton url={catalog.manager_url} />
    </>
  );
}

export function ManagerButton({ url }: { url: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        tap();
        openManager(url);
      }}
      className="glass mt-4 block w-full rounded-card px-4 py-3.5 text-center text-sm font-[620] text-link transition-[background-color,transform] duration-150 ease-glass active:scale-[.985] active:bg-glass-active"
    >
      Написать менеджеру
    </button>
  );
}

/* ---------------------------------------------------------------- раздел */

/** Стабильный холодный оттенок раздела: цвет владеет полем категории. */
function sectionHue(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return 150 + (hash % 130); // 150–280: бирюза → синий → фиолет, без тёплого
}



export function ProductGrid({
  list,
  onOpen,
}: {
  list: ProductList;
  onOpen: (product: Product) => void;
}) {
  const hue = sectionHue(list.title);

  return (
    <>
      <div
        className="animate-fade mb-3 flex items-center gap-2.5 rounded-card px-3.5 py-2.5 text-[13px]"
        style={{
          background: `linear-gradient(90deg, hsl(${hue} 60% 50% / .16), transparent)`,
          border: '1px solid var(--color-glass-line)',
        }}
      >
        <span
          className="size-2 shrink-0 rounded-full"
          style={{ background: `hsl(${hue} 70% 62%)` }}
        />
        <b className="min-w-0 truncate font-[650]">{list.title}</b>
        <span className="ml-auto shrink-0 text-xs text-hint" data-numeric>
          {count(list.products.length, 'позиция', 'позиции', 'позиций')}
        </span>
      </div>

      {!list.products.length && <Note>Список пуст.</Note>}

      <div className="grid grid-cols-2 gap-2.5">
        {list.products.map((product, index) => {
          const badge = variantBadge(product, list);
          return (
            <button
              key={product.id}
              type="button"
              onClick={(event) => {
                tap();
                rememberTile(
                  event.currentTarget.querySelector('[data-shot]'),
                  product.image,
                );
                onOpen(product);
              }}
              style={{ animationDelay: `${index * 28}ms` }}
              className="glass wet-edge animate-rise overflow-hidden rounded-card text-left transition-transform duration-150 ease-glass active:scale-[.97]"
            >
              <div className="relative aspect-square overflow-hidden" data-shot>
                <Photo product={product} small />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-linear-to-t from-night/70 to-transparent" />
              </div>
              <div className="flex flex-col gap-1 p-2.5">
                <div className="line-clamp-3 text-[13px] leading-snug font-[600]">
                  {product.text.split('\n')[0]}
                </div>
                {badge && (
                  <div className="inline-flex w-fit items-center gap-1 text-[11px] font-[600] text-mint">
                    <i className="animate-pulse-dot size-[5px] rounded-full bg-mint" />
                    {badge}
                  </div>
                )}
                <span
                  className="mt-0.5 text-sm font-[700] text-frost/90"
                  data-numeric
                >
                  {product.price} BYN
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {list.footer_message && <Note>{renderText(list.footer_message)}</Note>}
    </>
  );
}

/* ------------------------------------------------------------------ товар */

/** Одинаковые бонусы — в одну строку со счётчиком.

    Куплено десять «−20%» — это одна возможность, повторённая десять раз, а не
    десять разных. Выбирается конкретный экземпляр (первый), остальные ждут
    следующих заказов.
 */
export function groupRewards(rewards: Reward[]): { sample: Reward; ids: number[] }[] {
  const groups = new Map<string, { sample: Reward; ids: number[] }>();
  for (const item of rewards) {
    const key = `${item.kind}|${item.value ?? ''}|${item.title}`;
    const known = groups.get(key);
    if (known) known.ids.push(item.id);
    else groups.set(key, { sample: item, ids: [item.id] });
  }
  return [...groups.values()];
}

export function ProductScreen({
  product,
  list,
  onOrder,
  rewards,
  ordering,
  orderState,
  cart,
  onToast,
}: {
  product: Product;
  list: ProductList;
  onOrder: (variants: string[], rewardId: number | null) => void;
  /** Что лежит в кошельке: одна награда на заказ, выбирает человек */
  rewards: Reward[];
  ordering: boolean;
  orderState: 'idle' | 'sent';
  /** Режим корзины: вместо «Заказать» — степперы и «В корзину» */
  cart?: CartApi | null;
  onToast?: (text: string) => void;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  /** Штук на каждый выбранный вкус (или на товар без вкусов — ключ '') */
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [asking, setAsking] = useState(false);
  const [reward, setReward] = useState<Reward | null>(null);
  const [picking, setPicking] = useState(false);
  const shotRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Подсказка живёт ровно столько, сколько живёт тост, и гаснет сама
  useEffect(() => {
    if (!asking) return;
    const timer = setTimeout(() => setAsking(false), 2600);
    return () => clearTimeout(timer);
  }, [asking]);

  // Карточка не открывается заново — она разворачивается из нажатой плитки
  useEffect(() => {
    const box = shotRef.current;
    const target =
      box?.querySelector<HTMLElement>('img') ??
      (box?.firstElementChild as HTMLElement | null) ??
      null;
    runMorph(target);
  }, [product.id]);

  const price = Number(String(product.price).replace(',', '.'));
  const total = useMemo(() => {
    if (!Number.isFinite(price) || price <= 0) return null;
    if (cart) {
      // В корзинном режиме — сумма по счётчикам выбранных вкусов
      const picks = product.variants.length > 0 ? chosen : [''];
      const pieces = picks.reduce((sum, variant) => sum + (counts[variant] || 1), 0);
      return pieces > 1 ? { sum: Math.round(price * pieces * 100) / 100, pieces } : null;
    }
    if (chosen.length > 1) {
      return { sum: Math.round(price * chosen.length * 100) / 100, pieces: chosen.length };
    }
    return null;
  }, [chosen, price, cart, counts, product.variants.length]);

  // Со скидкой: процент виден до отправки, а не после разговора с менеджером
  const base = total?.sum ?? (Number.isFinite(price) && price > 0 ? price : null);
  const discounted = useMemo(() => {
    if (base === null || reward?.kind !== 'percent' || !reward.value) return null;
    return Math.round((base * (100 - reward.value)) / 100 * 100) / 100;
  }, [base, reward]);

  const toggle = (variant: string) => {
    tap();
    setAsking(false);
    setChosen((previous) =>
      previous.includes(variant)
        ? previous.filter((item) => item !== variant)
        : [...previous, variant],
    );
    setCounts((previous) => ({ ...previous, [variant]: previous[variant] || 1 }));
  };

  /** Потолок позиции с учётом уже лежащего в корзине. */
  const ceilingOf = (variant: string) =>
    cart
      ? Math.max(0, cart.maxFor(product.id, variant) - cart.inCart(product.id, variant))
      : 99;

  const bump = (variant: string, delta: number) => {
    const now = counts[variant] || 1;
    const next = now + delta;
    const ceiling = ceilingOf(variant);
    if (next > ceiling) {
      tap('error');
      onToast?.(
        ceiling < (cart?.info.cap ?? 99)
          ? 'Больше добавить нельзя'
          : `До ${cart?.info.cap} шт. Для оптовых заказов свяжитесь с менеджером`,
      );
      return;
    }
    // Минус на последней штуке снимает вкус: упор в единицу заставлял
    // человека искать, чем отменить выбор, — а минус и есть отмена
    if (next < 1) {
      if (!variant) return; // товар без вкусов: снимать нечего
      tap();
      setChosen((previous) => previous.filter((item) => item !== variant));
      setCounts((previous) => {
        const rest = { ...previous };
        delete rest[variant];
        return rest;
      });
      return;
    }
    tap();
    setCounts((previous) => ({ ...previous, [variant]: next }));
  };

  const intoCart = () => {
    if (!cart) return;
    if (product.variants.length > 0 && chosen.length === 0) {
      flushSync(() => setAsking(true));
      tap('error');
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      return;
    }
    const picks = product.variants.length > 0 ? chosen : [''];
    const title = product.text.split(String.fromCharCode(10))[0] ?? 'товар';
    let added = 0;
    for (const variant of picks) {
      const qty = Math.min(counts[variant] || 1, ceilingOf(variant));
      if (qty <= 0) {
        onToast?.('Больше добавить нельзя');
        continue;
      }
      cart.add({
        product_id: product.id,
        list_id: list.id,
        title,
        price: String(product.price),
        qty,
        variant: variant || undefined,
        thumb: product.thumb ?? product.image ?? null,
      });
      added += 1;
    }
    // Ничего не легло — не рапортуем успехом поверх отказа и не сбрасываем
    // выбор: человек видит, что упёрся в потолок, и правит количество
    if (!added) {
      tap('error');
      return;
    }
    tap('success');
    onToast?.('В корзине');
    setChosen([]);
    setCounts({});
  };

  /** Довести до глаз весь список вкусов, а не его середину.
   *
   * `scrollIntoView(center)` на списке выше экрана оставляет последние ряды
   * под липкой панелью — а именно их человек ещё не видел. Считаем сами:
   * прокручиваем ровно настолько, чтобы низ списка встал выше всего, что
   * приклеено к низу экрана. Панель заказа не одна: следом появится тост с
   * подсказкой, и он тоже накрывает последний ряд. */
  const submit = () => {
    if (product.variants.length > 0 && chosen.length === 0) {
      /* Кнопка липкая, а вкусы могут быть выше сгиба: человек жмёт «Заказать»,
         не видя, чего от него хотят. Везём страницу в самый низ — так весь
         список оказывается перед глазами.

         Подсказку показываем синхронно (`flushSync`): она добавляет панели
         высоты, и без этого прокрутка упёрлась бы в старый размер страницы. */
      flushSync(() => setAsking(true));
      tap('error');
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      return;
    }
    onOrder(chosen, reward?.id ?? null);
  };

  return (
    <>
      <div className="glass wet-edge overflow-hidden rounded-layer">
        <div className="aspect-square" ref={shotRef}>
          <Photo product={product} />
        </div>
        <div className="p-4">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-glass px-3 py-1.5 text-xs font-[620] text-mint">
            <span className="animate-pulse-dot size-[7px] rounded-full bg-mint" />
            В наличии
          </div>
          <div
            className="animate-fade text-[15px] leading-relaxed whitespace-pre-wrap"
            style={{ animationDelay: '120ms' }}
          >
            {product.text}
          </div>
        </div>
      </div>

      {product.variants.length > 0 && (
        <>
          {/* Фото ведёт морф, остальное подходит следом — с задержкой ровно
              на длину полёта, иначе два движения спорят друг с другом */}
          <h2
            className="animate-rise mt-4 mb-1 text-lg font-[680] tracking-[-.02em]"
            style={{ animationDelay: '120ms' }}
          >
            {variantLabel(list)}
          </h2>
          <div
            ref={chipsRef}
            className={`animate-rise flex flex-wrap gap-2 ${asking ? 'animate-[nudge_.3s]' : ''}`}
            style={asking ? undefined : { animationDelay: '160ms' }}
          >
            {product.variants.map((variant) => {
              const on = chosen.includes(variant);
              return (
                <button
                  key={variant}
                  type="button"
                  data-variant={variant}
                  data-chosen={on ? 'yes' : 'no'}
                  onClick={() => toggle(variant)}
                  className={[
                    // Запас под галочку заложен в паддинги заранее: чип сразу
                    // финальной ширины, при выборе двигается только текст
                    'inline-flex items-center justify-center rounded-full border text-sm font-[550]',
                    'transition-[background-color,color,border-color,padding,transform] duration-150 ease-glass',
                    'active:scale-[.94]',
                    on
                      ? 'border-transparent bg-linear-135 from-mint to-mint-deep py-2.5 pr-3.5 pl-3.5 text-mint-ink shadow-[0_4px_14px_rgba(43,217,196,.28)]'
                      : `bg-glass py-2.5 pr-[26.5px] pl-[26.5px] text-frost ${asking ? 'border-danger' : 'border-glass-line'}`,
                  ].join(' ')}
                >
                  <span
                    className={[
                      'grid place-items-center overflow-hidden rounded-full transition-all duration-200 ease-glass',
                      on
                        ? 'mr-2 size-[17px] scale-100 opacity-100 outline outline-mint-ink -outline-offset-[1.5px] bg-mint-ink/20'
                        : 'mr-0 size-0 scale-50 opacity-0',
                    ].join(' ')}
                  >
                    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12l5 5L20 7" />
                    </svg>
                  </span>
                  {variant}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Подсказка живёт внутри панели, а не отдельным тостом снизу: два
          независимо приклеенных к низу слоя неизбежно лезли друг на друга и на
          последний ряд вкусов. Панель растёт вверх — накрыть собой кнопку
          заказа ей нечем. */}
      <div
        ref={barRef}
        style={{ animationDelay: '200ms' }}
        className="animate-rise sticky bottom-[var(--above-tabs)] z-10 mt-6 flex flex-col gap-2.5 rounded-layer border border-glass-line bg-night-2/[.82] p-3 backdrop-blur-[16px] layer-shadow"
      >
        {asking && (
          <div className="animate-fade rounded-xl bg-danger/15 px-3 py-2 text-center text-[13px] font-[620] text-danger">
            Выберите {variantLabel(list).toLowerCase()}
          </div>
        )}

        {/* Режим корзины: у каждого выбранного вкуса своё количество — так
            «2 манго и 3 вишни» собираются в один присест, без хождений */}
        {cart && (chosen.length > 0 || product.variants.length === 0) && (
          <div className="grid gap-1.5">
            {(product.variants.length > 0 ? chosen : ['']).map((variant) => (
              <div
                key={variant || 'solo'}
                className="flex items-center justify-between gap-2 rounded-xl bg-glass px-3 py-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-[600]">
                  {variant || 'Количество'}
                </span>
                {/* shrink-0 обязателен: длинный вкус иначе сжимает степпер,
                    и плюс уезжает за край строки */}
                <span className="inline-flex shrink-0 items-center gap-2.5">
                  <button
                    type="button"
                    aria-label="Убавить"
                    onClick={() => bump(variant, -1)}
                    className="grid size-7 place-items-center rounded-full bg-glass-2 text-lg text-frost active:bg-glass-active"
                  >
                    −
                  </button>
                  <span className="min-w-4 text-center text-[14px] font-[680]" data-numeric>
                    {counts[variant] || 1}
                  </span>
                  <button
                    type="button"
                    aria-label="Прибавить"
                    onClick={() => bump(variant, 1)}
                    className="grid size-7 place-items-center rounded-full bg-glass-2 text-lg text-frost active:bg-glass-active"
                  >
                    +
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Награда предлагается до отправки, а не всплывает у менеджера:
            человек должен видеть итог до того, как нажмёт «Заказать» */}
        {rewards.length > 0 && (
          <button
            type="button"
            onClick={() => {
              tap();
              if (rewards.length === 1) setReward(reward ? null : rewards[0]!);
              else setPicking((was) => !was);
            }}
            className={[
              'flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px]',
              'transition-colors duration-150',
              reward ? 'bg-gold/15 text-gold-2' : 'bg-glass text-hint',
            ].join(' ')}
          >
            <span className="text-base">🎁</span>
            <span className="min-w-0 flex-1 truncate font-[620]">
              {reward
                ? reward.title
                : `Применить бонус · ${groupRewards(rewards).length}`}
            </span>
            <span className="shrink-0 text-xs opacity-70">
              {reward ? 'убрать' : 'выбрать'}
            </span>
          </button>
        )}

        {picking && rewards.length > 1 && (
          /* Одинаковые бонусы — одной строкой со счётчиком: после закупки
             впрок список из десяти «−20%» читался как ошибка, а не выбор.
             Выше шести видимых строк — прокрутка внутри списка */
          <div className="animate-fade grid max-h-[264px] gap-1.5 overflow-y-auto overscroll-contain">
            {groupRewards(rewards).map(({ sample, ids }) => (
              <button
                key={sample.id}
                type="button"
                onClick={() => {
                  tap();
                  setReward(sample);
                  setPicking(false);
                }}
                className="glass flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] active:bg-glass-active"
              >
                <span className="min-w-0 flex-1 truncate">
                  {sample.kind === 'percent'
                    ? `−${Math.round(sample.value ?? 0)}% · `
                    : '🎁 '}
                  {sample.title}
                </span>
                {ids.length > 1 && (
                  <span
                    className="shrink-0 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-[700] text-gold-2"
                    data-numeric
                  >
                    ×{ids.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <span className="px-3 py-2 text-[15px] font-[650]" data-numeric data-price>
            {total !== null && (
              /* Штук, а не вкусов: «17 × 5» при двух вкусах по 2 и 3 */
              <span className="block text-[11px] font-[600] text-hint">
                {product.price} × {total.pieces}
              </span>
            )}
            {discounted !== null ? (
              <>
                <span className="mr-1.5 text-[13px] font-[600] text-hint line-through">
                  {base}
                </span>
                <span className="text-gold-2">{discounted} BYN</span>
              </>
            ) : (
              `${total?.sum ?? product.price} BYN`
            )}
          </span>
          <Button
            data-order
            className="min-w-0 flex-1 truncate"
            disabled={!cart && (ordering || orderState === 'sent')}
            onClick={cart ? intoCart : submit}
          >
            {cart
              ? '🛒 В корзину'
              : orderState === 'sent'
                ? 'Отправлено'
                : ordering
                  ? 'Отправляю…'
                  : chosen.length > 1
                    ? `Заказать · ${chosen.length}`
                    : 'Заказать'}
          </Button>
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------------------- сообщение */

export function MessageScreen({ text, managerUrl }: { text: string; managerUrl: string }) {
  return (
    <>
      <RichBody text={text} className="glass wet-edge screen-in rounded-card p-4" />
      <ManagerButton url={managerUrl} />
    </>
  );
}

/* ------------------------------------------------------------------ поиск */

export function SearchResults({
  catalog,
  query,
  onOpen,
}: {
  catalog: Catalog;
  query: string;
  onOpen: (product: Product, list: ProductList) => void;
}) {
  const needle = query.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!needle) return [];
    const found: { product: Product; list: ProductList }[] = [];
    for (const list of Object.values(catalog.lists)) {
      for (const product of list.products) {
        const haystack = `${product.text} ${product.variants.join(' ')}`.toLowerCase();
        if (haystack.includes(needle)) found.push({ product, list });
      }
    }
    return found;
  }, [catalog, needle]);

  if (!hits.length) {
    return <Note>Ничего не нашлось. Попробуйте другое слово.</Note>;
  }

  return (
    <>
      <Note>
        Нашлось: {hits.length}
      </Note>
      <div className="grid grid-cols-2 gap-2.5">
        {hits.map(({ product, list }, index) => (
          <button
            key={`${list.id}-${product.id}`}
            type="button"
            style={{ animationDelay: `${Math.min(index, 12) * 28}ms` }}
            onClick={(event) => {
              tap();
              rememberTile(
                event.currentTarget.querySelector('[data-shot]'),
                product.image,
              );
              onOpen(product, list);
            }}
            className="glass wet-edge animate-rise overflow-hidden rounded-card text-left transition-transform duration-150 ease-glass active:scale-[.97]"
          >
            <div className="aspect-square overflow-hidden" data-shot>
              <Photo product={product} small />
            </div>
            <div className="flex flex-col gap-1 p-2.5">
              <div className="line-clamp-2 text-[13px] leading-snug font-[600]">
                {product.text.split('\n')[0]}
              </div>
              <span className="text-[11px] text-hint">{list.title}</span>
              <span className="text-sm font-[700]" data-numeric>
                {product.price} BYN
              </span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
